import { useEffect, useRef } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import useAuth from 'hooks/useAuth';
import { connectSocket, disconnectSocket } from 'utils/socket';
import { queryClient } from 'queryClient';

// hooks/useAuth is still a legacy .js module (out of scope, see AGENTS.md); this is the slice
// this hook consumes.
interface UseSocketAuth {
  isLoggedIn: boolean;
}

export function useSocket(): void {
  const { isLoggedIn } = useAuth() as unknown as UseSocketAuth;
  const connected = useRef(false);

  useEffect(() => {
    if (!isLoggedIn) {
      if (connected.current) {
        disconnectSocket();
        connected.current = false;
      }
      return;
    }

    if (connected.current) return;

    async function connect() {
      try {
        const token = await firebase.auth().currentUser?.getIdToken();
        if (!token) return;

        const socket = connectSocket(token);
        connected.current = true;

        // The server sends one generic 'ui:refresh' with a scope; each scope maps to the
        // query keys it invalidates. This used to re-broadcast as window CustomEvents that
        // individual screens listened for — TanStack Query's cache is what that indirection
        // was standing in for (AGENTS.md §1, axis 5).
        socket.on('ui:refresh', ({ scope }) => {
          switch (scope) {
            case 'shipments':
              queryClient.invalidateQueries({ queryKey: ['shipments'] });
              queryClient.invalidateQueries({ queryKey: ['dashboard'] });
              break;
            case 'quotes':
              queryClient.invalidateQueries({ queryKey: ['quotes'] });
              break;
            default:
              // 'profile' and 'documents' had no listener on this app; nothing to invalidate.
              break;
          }
        });

        // Token expires every hour — reconnect with fresh token on auth rejection
        socket.on('connect_error', async (err) => {
          if (err.message === 'Unauthorized') {
            try {
              const newToken = await firebase.auth().currentUser?.getIdToken(true);
              if (newToken) {
                socket.auth = { token: newToken };
                socket.connect();
              }
            } catch (tokenErr) {
              console.warn('[ws] token refresh failed:', (tokenErr as Error).message);
            }
          }
        });
      } catch (err) {
        console.warn('[ws] connect failed:', (err as Error).message);
      }
    }

    connect();

    return () => {
      disconnectSocket();
      connected.current = false;
    };
  }, [isLoggedIn]);
}
